import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { Button } from "components/ui/button";
import { IntlContext } from "context";
import { cn } from "lib/utils";

import { formatOptional, subcategoryBase } from "../subcategoryIntl";
import type { SubCategoryCategoryKey } from "../types";

const HERO_GRADIENT: Record<SubCategoryCategoryKey, string> = {
    housekeeping: "bg-gradient-to-br from-violet-950 via-violet-900 to-violet-800",
    handiwork: "bg-gradient-to-br from-amber-950 via-amber-900 to-amber-800",
    gardening: "bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-800",
    assistance: "bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800",
    childcare: "bg-gradient-to-br from-pink-950 via-pink-900 to-pink-800",
    private_tutoring: "bg-gradient-to-br from-teal-950 via-teal-900 to-teal-800",
};

interface SubCategoryHeroProps {
    categoryKey: SubCategoryCategoryKey;
    subcategoryKey: string;
    providersHref: string;
}

const SubCategoryHero: React.FC<SubCategoryHeroProps> = ({ categoryKey, subcategoryKey, providersHref }) => {
    const intl = useContext(IntlContext);
    const base = subcategoryBase(categoryKey, subcategoryKey);
    const { formatMessage, hasMessage } = intl;

    const title = useMemo(() => {
        const titleKey = `${base}.title`;
        const customTitle = formatOptional(hasMessage, formatMessage, titleKey);
        return customTitle ?? formatMessage(`${base}.description_short`);
    }, [base, formatMessage, hasMessage]);

    const subtitle = formatMessage(`${base}.description_short`);
    const ctaLabel = formatMessage("subcategoryPage.cta_find_provider");

    const disclaimer = categoryKey === "handiwork" ? formatMessage("subcategoryPage.handiwork_disclaimer") : undefined;

    const heroImage = useMemo(() => {
        const src = formatOptional(hasMessage, formatMessage, `${base}.img`);
        const alt = formatOptional(hasMessage, formatMessage, `${base}.img_alt`);
        return src ? { src, alt } : undefined;
    }, [base, formatMessage, hasMessage]);

    return (
        <section className="relative flex min-h-[280px] overflow-hidden sm:min-h-[450px]">
            {heroImage ? (
                <>
                    <img
                        src={heroImage.src}
                        alt={heroImage.alt ?? title}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div
                        className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/25"
                        aria-hidden
                    />
                </>
            ) : (
                <div className={cn("absolute inset-0", HERO_GRADIENT[categoryKey])} aria-hidden />
            )}

            <div className="relative h-full mx-auto flex w-full max-w-[1400px] flex-col justify-center px-4 my-auto sm:px-10 lg:px-20">
                <div className="max-w-xl text-primary-foreground">
                    <div className="space-y-4">
                        <h1 className="text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">{title}</h1>
                        <p className="text-sm leading-relaxed text-primary-foreground/90 sm:text-base">{subtitle}</p>
                        {disclaimer && (
                            <p className="text-xs leading-relaxed text-primary-foreground/80 sm:text-sm">
                                {disclaimer}
                            </p>
                        )}
                    </div>
                    <Button asChild size="xl" className="mt-10">
                        <Link to={providersHref}>
                            {ctaLabel}
                            <ArrowRight aria-hidden />
                        </Link>
                    </Button>
                </div>
            </div>
        </section>
    );
};

export default SubCategoryHero;
